import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * usePermissions – RBAC permission hook
 *
 * Reads the permissions array from Redux and exposes easy helpers
 * that any page / component can use.
 *
 * ─── Usage ──────────────────────────────────────────────────
 *
 *   import { usePermissions } from '../../hooks/usePermissions';
 *   import { PERMISSIONS }    from '../../constants/permissions';
 *
 *   const { hasPermission, canEdit, canRead } = usePermissions();
 *
 *   // Check any single permission
 *   hasPermission('app_perm_edit_order_list')   // true | false
 *
 *   // Sugar helpers – pass the *feature* part only
 *   canEdit('order_list')       // checks "app_perm_edit_order_list"
 *   canRead('order_list')       // checks "app_perm_read_order_list"
 *
 *   // Or pass the full codename constant
 *   hasPermission(PERMISSIONS.EDIT_ORDER_LIST)
 *
 * ─── In JSX ─────────────────────────────────────────────────
 *
 *   <Button disabled={!canEdit('order_list')}>Save</Button>
 *
 *   {canEdit('team') && <InviteButton />}
 *
 * @returns {{
 *   permissions: Array,
 *   permissionSet: Set<string>,
 *   hasPermission: (codename: string) => boolean,
 *   canEdit: (feature: string) => boolean,
 *   canRead: (feature: string) => boolean,
 *   isSuperUser: boolean
 * }}
 */
export const usePermissions = () => {
  const reduxPermissions = useSelector((state) => state?.permissions?.permissions || []);
  const userInfo = useSelector((state) => state?.User?.userInfo);
  const isSuperUser = userInfo?.profile?.user?.is_superuser || false;

  // Prefer fresh permissions from getUserProfile, fall back to login-time Redux permissions
  const profilePermissions = userInfo?.profile?.user?.group?.permissions;
  const permissions = Array.isArray(profilePermissions) && profilePermissions.length > 0
    ? profilePermissions
    : reduxPermissions;

  // O(1) look-ups via Set
  const permissionSet = useMemo(
    () => new Set(permissions.map((p) => p.codename)),
    [permissions]
  );

  /**
   * Check if the user has a specific permission codename.
   * Superusers always return true.
   * @param {string} codename  e.g. "app_perm_edit_order_list"
   */
  const hasPermission = (codename) => {
    if (isSuperUser) return true;
    return permissionSet.has(codename);
  };

  /**
   * Shorthand: check EDIT permission for a feature.
   * @param {string} feature  e.g. "order_list", "team", "branding"
   */
  const canEdit = (feature) => hasPermission(`app_perm_edit_${feature}`);

  /**
   * Shorthand: check READ permission for a feature.
   * @param {string} feature  e.g. "order_list", "team", "branding"
   */
  const canRead = (feature) => hasPermission(`app_perm_read_${feature}`);

  return {
    permissions,
    permissionSet,
    hasPermission,
    canEdit,
    canRead,
    isSuperUser,
  };
};
